import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Hourglass, CalendarClock, Radio, MapPin, Plane, TrainFront, Users, Briefcase,
  Navigation, ChevronRight, Clock, Star, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { DriverShell } from "@/components/Shells";
import { Spinner, EmptyState } from "@/components/States";
import Button from "@/components/Button";
import { api } from "@/lib/api";
import { isPhone } from "@/lib/device";
import { money, miles, fmtDateTime, cn } from "@/lib/format";

function Route({ ride }) {
  return (
    <div className="rounded-2xl bg-secondary p-3">
      <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
        {ride.pickup.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.pickup.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-success" />}
        <span className="truncate">{ride.pickup.label}</span>
      </p>
      <div className="my-1 ml-2 h-3 border-l-2 border-dashed border-border" />
      <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
        {ride.destination.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.destination.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-brand-600" />}
        <span className="truncate">{ride.destination.label}</span>
      </p>
      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span>{miles(ride.distance_miles)}</span>
        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ride.passengers ?? 1}</span>
        <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {ride.bags ?? 0}</span>
        {ride.airport_info?.airline && <span className="flex items-center gap-1 font-semibold text-brand-600"><Plane className="h-3.5 w-3.5" /> {ride.airport_info.airline}</span>}
        {ride.required_class_label && <span className="rounded-full bg-accent px-2 py-0.5 font-semibold text-brand-700 dark:text-brand-400">{ride.required_class_label}</span>}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, tint, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">
        <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", tint)}><Icon className="h-4 w-4" /></span>
        {title} <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{count}</span>
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export default function DriverRides() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const phone = isPhone();

  const load = useCallback(() => {
    api("/driver/offers").then(setData).catch(() => setData({ pending_offers: [], scheduled: [], active: [] }));
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, [load]);

  const withdraw = async (ride) => {
    setBusy(ride.id);
    try { await api(`/rides/${ride.id}/withdraw-offer`, { method: "POST" }); toast.success("Offer withdrawn"); load(); }
    catch (e) { toast.error(e?.message || "Failed"); }
    finally { setBusy(null); }
  };

  const startRide = async (ride) => {
    if (!isPhone()) { toast.error("Start your trip from the Getaride app on your phone."); return; }
    setBusy(ride.id);
    try {
      await api(`/rides/${ride.id}/driver-status`, { method: "POST", body: { status: "driver_enroute" } });
      navigate(`/driver-trip/${ride.id}`);
    } catch (e) { toast.error(e?.message || "Could not start ride."); setBusy(null); }
  };

  const cancelRide = async (ride) => {
    if (!window.confirm("Cancel this scheduled ride?")) return;
    setBusy(ride.id);
    try { await api(`/rides/${ride.id}/cancel`, { method: "POST" }); toast.info("Ride cancelled"); load(); }
    catch (e) { toast.error(e?.message || "Failed"); }
    finally { setBusy(null); }
  };

  if (!data) {
    return <DriverShell><div className="flex justify-center py-20"><Spinner className="h-7 w-7 text-brand-600" /></div></DriverShell>;
  }

  const { pending_offers: pending, scheduled, active } = data;
  const empty = !pending.length && !scheduled.length && !active.length;

  return (
    <DriverShell>
      <div className="mx-auto max-w-2xl px-5 py-6">
        <h1 className="mb-5 text-2xl font-extrabold text-foreground">Your rides</h1>

        {empty ? (
          <EmptyState icon={CalendarClock} title="No rides yet" subtitle="Bid on requests from the Drive tab. Your offers and scheduled trips will show up here." />
        ) : (
          <>
            {active.length > 0 && (
              <Section icon={Radio} title="Active" count={active.length} tint="bg-success/15 text-success">
                {active.map((r) => (
                  <button key={r.id} data-testid={`active-ride-${r.id}`} onClick={() => navigate(`/driver-trip/${r.id}`)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition-colors hover:border-brand-400">
                    <span className="flex h-3 w-3 shrink-0"><span className="h-3 w-3 animate-pulse rounded-full bg-success" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-foreground">{r.customer_name} · {money(r.final_fare)}</p>
                      <p className="truncate text-sm text-muted-foreground">{r.pickup.label} → {r.destination.label}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-brand-600" />
                  </button>
                ))}
              </Section>
            )}

            <Section icon={CalendarClock} title="Scheduled" count={scheduled.length} tint="bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
              {scheduled.length === 0 ? <p className="text-sm text-muted-foreground">No scheduled rides.</p> : scheduled.map((r) => (
                <div key={r.id} data-testid={`scheduled-ride-${r.id}`} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
                      <Clock className="h-3.5 w-3.5" /> {fmtDateTime(r.scheduled_ts) || r.scheduled_time}
                    </span>
                    <span className="font-num text-lg font-extrabold text-foreground">{money(r.final_fare)}</span>
                  </div>
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    <span className="font-semibold text-foreground">{r.customer_name}</span>
                    <span className="text-muted-foreground">· {Number(r.customer_rating || 5).toFixed(1)}</span>
                  </div>
                  <Route ride={r} />
                  <div className="mt-3 flex gap-3">
                    <Button className="flex-1" loading={busy === r.id} disabled={!phone} onClick={() => startRide(r)} data-testid={`start-scheduled-${r.id}`}>
                      <Navigation className="h-4 w-4" /> Start ride
                    </Button>
                    <button onClick={() => cancelRide(r)} data-testid={`cancel-scheduled-${r.id}`}
                      className="rounded-xl border-2 border-border px-4 text-sm font-semibold text-destructive hover:border-destructive/50">Cancel</button>
                  </div>
                  {!phone && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground" data-testid={`start-phone-only-${r.id}`}>
                      <Phone className="h-3.5 w-3.5" /> Start and drive this trip from the Getaride app on your phone.
                    </p>
                  )}
                </div>
              ))}
            </Section>

            <Section icon={Hourglass} title="Pending offers" count={pending.length} tint="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
              {pending.length === 0 ? <p className="text-sm text-muted-foreground">No offers awaiting a rider.</p> : pending.map((r) => (
                <div key={r.id} data-testid={`pending-offer-${r.id}`} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                      <Hourglass className="h-3.5 w-3.5" /> Awaiting rider
                    </span>
                    <span className="font-num text-lg font-extrabold text-foreground">{money(r.offer_fare)}</span>
                  </div>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    {r.when === "scheduled" ? `Scheduled · ${fmtDateTime(r.scheduled_ts) || r.scheduled_time}` : "Pickup now"}
                  </p>
                  <Route ride={r} />
                  <button onClick={() => withdraw(r)} disabled={busy === r.id} data-testid={`withdraw-offer-${r.id}`}
                    className="mt-3 w-full rounded-xl border-2 border-border py-2.5 text-sm font-semibold text-destructive hover:border-destructive/50 disabled:opacity-50">
                    Withdraw offer
                  </button>
                </div>
              ))}
            </Section>
          </>
        )}
      </div>
    </DriverShell>
  );
}
